import { Card, CardBody, CardHeader, Flex, Text } from "@backstage/ui";
import type {
	BundleInventoryItem,
	BundleInventoryType,
	ColleagueBaseSummary,
} from "@internal/backstage-plugin-avd-common";
import { ReactNode } from "react";

import { bundleIacVersion, type BundleInventoryDescriptor } from "./bundleDescriptors";
import { BundleInventoryTable } from "./bundleInventory";
import { absoluteTime, ownerLabel } from "./inventoryCells";

/** The base's line in a colleague's header: subscription, version against the build's stamp, drift. */
export function baseSummaryText(base: ColleagueBaseSummary | undefined): string {
	if (!base) return "No base recorded.";
	if (base.destroyedAt) return `Base destroyed ${absoluteTime(base.destroyedAt)}.`;
	const version = bundleIacVersion({
		bundleType: "base",
		iacVersion: base.iacVersion,
		status: "",
	});
	return [
		`Subscription ${base.subscriptionId ?? "unknown"}`,
		`base IaC ${version.state}${base.iacVersion ? ` (${base.iacVersion})` : ""}`,
		`drift ${base.driftStatus ?? "never checked"}`,
	].join(" · ");
}

/** Rows grouped by colleague: one card each, with the colleague's base above their bundles. */
export function ColleagueGroups(
	props: Readonly<{
		items: readonly BundleInventoryItem[];
		bases: readonly ColleagueBaseSummary[];
		types: readonly BundleInventoryType[];
		descriptors: readonly BundleInventoryDescriptor[];
		now: Date;
		emptyContent: ReactNode;
		/** Also list colleagues with a base but no listed bundle; off while a filter narrows the rows. */
		includeBaseOnly?: boolean;
		onOpen: (item: BundleInventoryItem) => void;
	}>,
): ReactNode {
	const bySlug = new Map<string, BundleInventoryItem[]>();
	for (const item of props.items) bySlug.set(item.slug, [...(bySlug.get(item.slug) ?? []), item]);
	if (props.includeBaseOnly) {
		for (const base of props.bases) if (!bySlug.has(base.slug)) bySlug.set(base.slug, []);
	}
	if (bySlug.size === 0) return <>{props.emptyContent}</>;
	const slugs = [...bySlug.keys()].sort((a, b) => a.localeCompare(b));

	return (
		<Flex direction="column" gap="3">
			{slugs.map((slug) => {
				const rows = bySlug.get(slug)!;
				const name = rows[0] ? (rows[0].owner.displayName ?? ownerLabel(rows[0])) : slug;
				return (
					<Card key={slug}>
						<CardHeader>
							<Text as="h3" variant="title-small">
								{name}
							</Text>
							<Text as="div" variant="body-small" color="secondary">
								{slug}
							</Text>
						</CardHeader>
						<CardBody>
							<Text as="p" variant="body-small" color="secondary">
								{baseSummaryText(props.bases.find((b) => b.slug === slug))}
							</Text>
							{rows.length === 0 ? (
								<Text as="p">No bundles.</Text>
							) : (
								<BundleInventoryTable
									compact
									items={rows}
									types={props.types}
									descriptors={props.descriptors}
									now={props.now}
									onOpen={props.onOpen}
								/>
							)}
						</CardBody>
					</Card>
				);
			})}
		</Flex>
	);
}
